import type { Action, Reducer } from 'redux';
import { IApplicationState, IAppThunkAction, IAnalyticsAction } from './';
import type { CallHistoryMethodAction } from 'connected-react-router';
import GiftVoucherService from '../service/giftvouchers';
import { IGiftVoucherConfig } from '../interfaces/IGiftVoucherConfig';
import { Routes, navigateTo } from '../helpers/routes';
import { ICharge } from '../interfaces/ICharge';
import { IGiftVoucher } from '../interfaces/GiftVouchers/IGiftVoucher';
import { GoogleTagManagerEvents } from '../helpers/googleTagManager';
import Logging from '../service/logging';
import _ from 'lodash';
import BrazeEvents from '../helpers/braze';
import { PurchaseTypes } from './PurchaseTypes';
 import { appInsights } from "../Logging/appInsights";
import { ICurrency } from '../interfaces/ICurrency';
import ISite from '../interfaces/ISite';
import { Constants } from '../types/Constants';
import { IUpdateGiftVoucherRequest } from '../interfaces/GiftVouchers/IUpdateGiftVoucherRequest';
// Customer contact data (email, name, phone) lives in the Customer store, not here.
// GiftVoucher thunks read from appState.customer when making API calls. This is temporary
// coupling — when a dedicated customer sync API is added, these reads should be removed.
// Do not add customer contact fields to IGiftVoucherState.
import { actionCreators as CustomerActions, formatPhoneNumber } from './Customer';

export enum GiftVoucherStatus {
    Unknown = 0,
    Pending = 1,
    Active = 2,
    Cancelled = 3,
    Refunded = 4,
    Expired = 5,
    Failed = 6,
    Completed = 7,
    Reserved = 8,
}

export interface IGiftVoucherState {
    loading: boolean;
    configLoading: boolean;
    failed: boolean;
    configDetails?: IGiftVoucherConfig;
    charge?: ICharge;
    giftVoucher?: IGiftVoucher;
    status: GiftVoucherStatus;
    selectedFixedAmountButtonIndex?: number;
    amount?: number;
    toEmail?: string;
    toFullName?: string;
    message?: string;
    selectedGiftVoucherAmountUI?: boolean;
    selectedGiftVoucherDetailsUI?: boolean;
    toRecipientSelected?: boolean;
    selectedVoucherCurrency?: ICurrency;
    recipientDetailsValid: boolean;
}

export const RECORD_TO_EMAIL_ADDRESS: 'EMAIL_FIELD_CHANGED' = 'EMAIL_FIELD_CHANGED';
export const RECORD_TO_FULL_NAME: 'NAME_FIELD_CHANGED' = 'NAME_FIELD_CHANGED';
export const RECORD_MESSAGE: 'MESSAGE_FIELD_CHANGED' = 'MESSAGE_FIELD_CHANGED';

export const AMOUNT_ENTERED: 'CUSTOM_AMOUNT_ENTERED' = 'CUSTOM_AMOUNT_ENTERED';
export const BUTTON_SELECTED: 'BUTTON_SELECTED' = 'BUTTON_SELECTED';
export const CLEAR_FIELD: 'CLEAR_FIELD' = 'CLEAR_FIELD';

export const GIFT_VOUCHER_CONFIG_LOADING: 'GIFT_VOUCHER_CONFIG_LOADING' = 'GIFT_VOUCHER_CONFIG_LOADING';
export const GIFT_VOUCHER_CONFIG_LOADED: 'GIFT_VOUCHER_CONFIG_LOADED' = 'GIFT_VOUCHER_CONFIG_LOADED';

export const RECIPIENT_SELECTED: 'RECIPIENT_SELECTED' = 'RECIPIENT_SELECTED';

export const RESERVE_GIFT_VOUCHER_SUCCESS: 'RESERVE_GIFT_VOUCHER_SUCCESS' = 'RESERVE_GIFT_VOUCHER_SUCCESS';
export const RESERVE_GIFT_VOUCHER_FAILED: 'RESERVE_GIFT_VOUCHER_FAILED' = 'RESERVE_GIFT_VOUCHER_FAILED';
export const RESERVE_GIFT_VOUCHER_REQUESTED: 'RESERVE_GIFT_VOUCHER_REQUESTED' = 'RESERVE_GIFT_VOUCHER_REQUESTED';

export const UPDATE_GIFT_VOUCHER_AMOUNT_SUCCESS: 'UPDATE_GIFT_VOUCHER_AMOUNT_SUCCESS' =
    'UPDATE_GIFT_VOUCHER_AMOUNT_SUCCESS';
export const UPDATE_GIFT_VOUCHER_AMOUNT_FAILED: 'UPDATE_GIFT_VOUCHER_AMOUNT_FAILED' =
    'UPDATE_GIFT_VOUCHER_AMOUNT_FAILED';
export const UPDATE_GIFT_VOUCHER_AMOUNT_LOADING: 'UPDATE_GIFT_VOUCHER_AMOUNT_LOADING' =
    'UPDATE_GIFT_VOUCHER_AMOUNT_LOADING';

export const MODIFY_GIFT_VOUCHER: 'MODIFY_GIFT_VOUCHER' = 'MODIFY_GIFT_VOUCHER';

export const CONFIRM_GIFT_VOUCHER_SUCCESS: 'CONFIRM_GIFT_VOUCHER_SUCCESS' = 'CONFIRM_GIFT_VOUCHER_SUCCESS';
export const CONFIRM_GIFT_VOUCHER_FAILED: 'CONFIRM_GIFT_VOUCHER_FAILED' = 'CONFIRM_GIFT_VOUCHER_FAILED';
export const CONFIRM_GIFT_VOUCHER_COMPLETE: 'CONFIRM_GIFT_VOUCHER_COMPLETE' = 'CONFIRM_GIFT_VOUCHER_COMPLETE';
export const CONFIRM_GIFT_VOUCHER_REQUESTED: 'CONFIRM_GIFT_VOUCHER_REQUESTED' = 'CONFIRM_GIFT_VOUCHER_REQUESTED';

export const CANCEL_GIFT_VOUCHER_SUCCESS: 'CANCEL_GIFT_VOUCHER_SUCCESS' = 'CANCEL_GIFT_VOUCHER_SUCCESS';
export const CANCEL_GIFT_VOUCHER_FAILED: 'CANCEL_GIFT_VOUCHER_FAILED' = 'CANCEL_GIFT_VOUCHER_FAILED';
export const CANCEL_GIFT_VOUCHER_COMPLETE: 'CANCEL_GIFT_VOUCHER_COMPLETE' = 'CANCEL_GIFT_VOUCHER_COMPLETE';
export const CANCEL_GIFT_VOUCHER_REQUESTED: 'CANCEL_GIFT_VOUCHER_REQUESTED' = 'CANCEL_GIFT_VOUCHER_REQUESTED';

//export const GIFT_VOUCHER_DETAILS_LOADED: 'GIFT_VOUCHER_DETAILS_LOADED' = 'GIFT_VOUCHER_DETAILS_LOADED';

export const FETCH_GIFT_VOUCHER_DETAILS_REQUESTED: 'FETCH_GIFT_VOUCHER_DETAILS_REQUESTED' = 'FETCH_GIFT_VOUCHER_DETAILS_REQUESTED';
export const FETCH_GIFT_VOUCHER_DETAILS_SUCCESS: 'FETCH_GIFT_VOUCHER_DETAILS_SUCCESS' = 'FETCH_GIFT_VOUCHER_DETAILS_SUCCESS';
export const FETCH_GIFT_VOUCHER_DETAILS_FAILED: 'FETCH_GIFT_VOUCHER_DETAILS_FAILED' = 'FETCH_GIFT_VOUCHER_DETAILS_FAILED';

export const CLEAR_GIFT_VOUCHER: 'CLEAR_GIFT_VOUCHER' = 'CLEAR_GIFT_VOUCHER';

export const CHANGE_USER: 'CHANGE_USER' = 'CHANGE_USER';

export const SELECT_VOUCHER_CURRENCY: 'SELECT_VOUCHER_CURRENCY' = 'SELECT_VOUCHER_CURRENCY';

export const SET_RECIPIENT_DETAILS_VALID: 'SET_RECIPIENT_DETAILS_VALID' = 'SET_RECIPIENT_DETAILS_VALID';

export interface IToEmailFieldChangedAction extends IAnalyticsAction {
    type: typeof RECORD_TO_EMAIL_ADDRESS;
    email: string;
}

export interface IToNameFieldChangedAction extends IAnalyticsAction {
    type: typeof RECORD_TO_FULL_NAME;
    name: string;
}

export interface IMessageFieldChangedAction extends IAnalyticsAction {
    type: typeof RECORD_MESSAGE;
    message: string;
}

export interface IAmountEnteredAction extends IAnalyticsAction {
    type: typeof AMOUNT_ENTERED;
    amount: number;
}

export interface IButtonSelectedAction extends IAnalyticsAction {
    type: typeof BUTTON_SELECTED;
    buttonIndex: number;
    amount: number;
}

export interface IClearFieldAction {
    type: typeof CLEAR_FIELD;
    amount: number;
}

export interface IGiftVoucherConfigLoadingAction {
    type: typeof GIFT_VOUCHER_CONFIG_LOADING;
}
export interface IGiftVoucherConfigLoadedAction {
    type: typeof GIFT_VOUCHER_CONFIG_LOADED;
    details?: IGiftVoucherConfig;
    success: boolean;
}

export interface IReserveGiftVoucherSuccessAction extends IAnalyticsAction {
    type: typeof RESERVE_GIFT_VOUCHER_SUCCESS;
    giftVoucher: IGiftVoucher;
    charge: ICharge;
}

export interface IReserveGiftVoucherFailedAction {
    type: typeof RESERVE_GIFT_VOUCHER_FAILED;
    failureMessageKey: string;
}

export interface IReserveGiftVoucherRequestedAction {
    type: typeof RESERVE_GIFT_VOUCHER_REQUESTED;
}

export interface IRecipientSelectedAction {
    type: typeof RECIPIENT_SELECTED;
    toRecipient: boolean;
}

export interface IUpdateGiftVoucherAmountSuccessAction {
    type: typeof UPDATE_GIFT_VOUCHER_AMOUNT_SUCCESS;
    giftVoucher: IGiftVoucher;
    charge: ICharge;
}

export interface IUpdateGiftVoucherAmountFailedAction {
    type: typeof UPDATE_GIFT_VOUCHER_AMOUNT_FAILED;
    failureMessageKey: string;
}

export interface IUpdateGiftVoucherAmountLoadingAction {
    type: typeof UPDATE_GIFT_VOUCHER_AMOUNT_LOADING;
}

export interface IConfirmGiftVoucherFailedAction {
    type: typeof CONFIRM_GIFT_VOUCHER_FAILED;
}

export interface IConfirmGiftVoucherSuccessAction extends IAnalyticsAction {
    type: typeof CONFIRM_GIFT_VOUCHER_SUCCESS;
}

export interface IConfirmGiftVoucherCompletedAction {
    type: typeof CONFIRM_GIFT_VOUCHER_COMPLETE;
}

export interface IConfirmGiftVoucherRequestedAction {
    type: typeof CONFIRM_GIFT_VOUCHER_REQUESTED;
}

export interface ICancelGiftVoucherSuccessAction {
    type: typeof CANCEL_GIFT_VOUCHER_SUCCESS;
}

export interface ICancelGiftVoucherFailedAction {
    type: typeof CANCEL_GIFT_VOUCHER_FAILED;
}

export interface ICancelGiftVoucherCompleteAction {
    type: typeof CANCEL_GIFT_VOUCHER_COMPLETE;
}

export interface ICancelGiftVoucherRequestedAction {
    type: typeof CANCEL_GIFT_VOUCHER_REQUESTED;
}

export interface IFetchGiftVoucherDetailsRequestedAction {
    type: typeof FETCH_GIFT_VOUCHER_DETAILS_REQUESTED;
}

export interface IFetchGiftVoucherDetailsSuccessAction {
    type: typeof FETCH_GIFT_VOUCHER_DETAILS_SUCCESS;
    details?: IGiftVoucher;
}

export interface IFetchGiftVoucherDetailsFailedAction {
    type: typeof FETCH_GIFT_VOUCHER_DETAILS_FAILED;
}

export interface IModifyGiftVoucherAction extends IAnalyticsAction {
    type: typeof MODIFY_GIFT_VOUCHER;
}

export interface IClearGiftVoucherAction {
    type: typeof CLEAR_GIFT_VOUCHER;
}

export interface IChangeUserAction extends IAnalyticsAction {
    type: typeof CHANGE_USER;
    giftVoucher: IGiftVoucher;
}

export interface ISelectVoucherCurrency {
    type: typeof SELECT_VOUCHER_CURRENCY;
    currency: ICurrency | undefined;
}

export interface ISetRecipientDetailsValidAction {
    type: typeof SET_RECIPIENT_DETAILS_VALID;
    isValid: boolean;
}

const undefinedState: IGiftVoucherState = {
    loading: false,
    configLoading: false,
    failed: false,
    configDetails: undefined,
    charge: undefined,
    giftVoucher: undefined,
    status: GiftVoucherStatus.Unknown,
    selectedFixedAmountButtonIndex: undefined,
    amount: undefined,
    toEmail: undefined,
    toFullName: undefined,
    message: undefined,
    selectedGiftVoucherAmountUI: false,
    selectedGiftVoucherDetailsUI: false,
    toRecipientSelected: undefined,
    selectedVoucherCurrency: undefined,
    recipientDetailsValid: false,
};

export type KnownGiftVoucherActions =
    | IToEmailFieldChangedAction
    | IToNameFieldChangedAction
    | IMessageFieldChangedAction
    | IAmountEnteredAction
    | IButtonSelectedAction
    | IClearFieldAction
    | IGiftVoucherConfigLoadingAction
    | IGiftVoucherConfigLoadedAction
    | IRecipientSelectedAction
    | IReserveGiftVoucherSuccessAction
    | IReserveGiftVoucherFailedAction
    | IReserveGiftVoucherRequestedAction
    | IUpdateGiftVoucherAmountSuccessAction
    | IUpdateGiftVoucherAmountFailedAction
    | IUpdateGiftVoucherAmountLoadingAction
    | IModifyGiftVoucherAction
    | IConfirmGiftVoucherFailedAction
    | IConfirmGiftVoucherSuccessAction
    | IConfirmGiftVoucherCompletedAction
    | IConfirmGiftVoucherRequestedAction
    | ICancelGiftVoucherSuccessAction
    | ICancelGiftVoucherFailedAction
    | ICancelGiftVoucherCompleteAction
    | ICancelGiftVoucherRequestedAction
    | IClearGiftVoucherAction
    | IFetchGiftVoucherDetailsRequestedAction
    | IFetchGiftVoucherDetailsSuccessAction
    | IFetchGiftVoucherDetailsFailedAction
    | IChangeUserAction
    | CallHistoryMethodAction
    | ISelectVoucherCurrency
    | ISetRecipientDetailsValidAction;

let inFlightUpdateGiftVoucher: AbortController | null = null;

export const actionCreators = {
    continuePendingGiftVoucher: (): IAppThunkAction<KnownGiftVoucherActions> => (dispatch, getState) => {
        // TODO this needs to also work in global mode (same as the reserve action)
        const appState = getState();
        const isEventBooking = false;
        const charge = appState?.booking?.charge;
        const selectedSite = appState?.sites?.selectedSite;
        const currentLocale = appState?.intl?.selectedLanguage.code;
        const selectedPurchaseType = PurchaseTypes[appState?.purchaseType?.selectedPurchaseType].toString();

        dispatch(navigateTo(Routes.Checkout, selectedSite?.urlKey));
        if (charge && selectedSite && currentLocale && selectedPurchaseType) {
            const isReservation = appState?.sites?.selectedSite?.reservationPortal ?? false;
            const isGiftVoucher = appState?.purchaseType?.selectedPurchaseType === PurchaseTypes.GiftVoucher;

            BrazeEvents.CheckoutInitiated(
                isEventBooking,
                charge,
                selectedSite,
                currentLocale,
                isReservation,
                isGiftVoucher,
                'N/A'
            );
        }
    },
    reserveRegionalGiftVoucher:
        (adBlockEnabled: boolean): IAppThunkAction<KnownGiftVoucherActions> =>
            (dispatch, getState) => {
                const appState = getState();

                //Global gift vouchers are just gift vouchers saved against harper street
                // harper street gift vouchers are usable by any other venue that uses the same currency as what the voucher was purchased against
                const magicVenue = appState.sites.globalSite;

                if (!magicVenue) {
                    const msg = "Could not find the magic global venue to stash the voucher against";
                    console.error(msg);
                    appInsights?.trackException(
                        { error: new Error(`Error reserving global gift voucher`), severityLevel: 2 },
                        { exception: msg, sourceFile: "GiftVoucher.tsx", product: "booking" });

                    dispatch({
                        type: RESERVE_GIFT_VOUCHER_FAILED,
                        failureMessageKey: 'checkout failed',
                    });
                    return;
                }

                // call thru to the shared/underlying reserve action
                console.log(`reserve global gift voucher calling thru to reserve gift voucher using override venue: ${magicVenue?.code} - ${magicVenue?.name}`);
                return dispatch(actionCreators.reserveGiftVoucher(adBlockEnabled, magicVenue));
            },
    reserveGiftVoucher:
    (adBlockEnabled: boolean, overrideSite?: ISite | undefined): IAppThunkAction<KnownGiftVoucherActions> =>
        (dispatch, getState) => {
            const appState: IApplicationState = getState();
            const giftVoucher = appState.giftVoucher;

            if (giftVoucher) {

                //regional gift vouchers use the override site
                const selectedSite = overrideSite ?? appState?.sites?.selectedSite; //use the magic global site if available otherwise use the users selected venue
                const isRegional = overrideSite !== undefined;
                const currencyTypeId = isRegional ? appState.giftVoucher.selectedVoucherCurrency?.id : undefined;

                const emailAddress = appState?.customer?.emailAddress;
                const name = appState?.customer?.fullName;
                const isEventBooking = false;
                const currentLocale = appState?.intl?.selectedLanguage.code;
                const selectedPurchaseType = PurchaseTypes[appState?.purchaseType?.selectedPurchaseType].toString();

                const isReservation = appState?.sites?.selectedSite?.reservationPortal ?? false;

                const isGiftVoucher = appState?.purchaseType?.selectedPurchaseType === PurchaseTypes.GiftVoucher ||
                    appState?.purchaseType?.selectedPurchaseType === PurchaseTypes.GlobalGiftVoucher;

                const expirationFromConfig = appState.giftVoucher.configDetails?.expiryYears;

                let expirationDate = undefined;

                // only non gobal vouchers from venues with a config have an expiary date

                if (!isRegional && expirationFromConfig !== undefined && expirationFromConfig !== null) {
                    expirationDate = new Date();
                    expirationDate.setFullYear(expirationDate.getFullYear() + expirationFromConfig);
                    expirationDate = expirationDate.toISOString();
                }

                if (selectedSite && currentLocale && selectedPurchaseType) {
                    GiftVoucherService.ReserveGiftVoucher(
                        giftVoucher,
                        name,
                        emailAddress,
                        selectedSite.siteId,
                        expirationDate,
                        adBlockEnabled,
                        isRegional,
                        currencyTypeId

                    )
                        .then((data) => {
                            dispatch({
                                type: RESERVE_GIFT_VOUCHER_SUCCESS,
                                giftVoucher: data.giftVoucher,
                                charge: data.charge,
                                googleTagManagerEvent: GoogleTagManagerEvents.ReserveGiftVoucher(data.charge, isRegional),
                                brazeEvent: BrazeEvents.CheckoutInitiated(
                                    isEventBooking,
                                    data.charge,
                                    selectedSite,
                                    currentLocale,
                                    isReservation,
                                    isGiftVoucher,
                                    'N/A'
                                ),
                            });
                            Logging.SetInvoiceId(data.giftVoucher.invoiceId?.toString());

                            if (isRegional) {
                                dispatch(navigateTo(Routes.Checkout));
                            }
                            else {
                                dispatch(navigateTo(Routes.Checkout, appState.sites.selectedSite?.urlKey));
                            }
                        })

                        .catch((error: Error) => {
                            dispatch({
                                type: RESERVE_GIFT_VOUCHER_FAILED,
                                failureMessageKey: 'checkout failed',
                            });
                            appInsights?.trackException(
                                { error: new Error(`Error reserving gift voucher`), severityLevel: 2 },
                                { exception: error, sourceFile: "GiftVoucher.tsx", product: "booking" });
                        });

                    dispatch({ type: RESERVE_GIFT_VOUCHER_REQUESTED });
                }
            }
        },
    confirmGiftVoucher:
        (
            paymentGatewayMetadata: any,
            paymentMethodReference: string,
            paymentMethodTypeId: number,
            adBlockDetected: boolean
        ): IAppThunkAction<any> =>
        (dispatch, getState) => {
            const appState: IApplicationState = getState();

            if (appState && appState.giftVoucher.giftVoucher && appState.giftVoucher.charge) {
                const giftVoucher = appState.giftVoucher.giftVoucher;
                const charge = appState.giftVoucher.charge;

                //move to processing page before starting
                dispatch(navigateTo(Routes.Processing));

                GiftVoucherService.ConfirmGiftVoucher(
                    charge,
                    giftVoucher,
                    paymentMethodReference,
                    paymentMethodTypeId,
                    paymentGatewayMetadata,
                    adBlockDetected
                )
                    .then((_data) => {
                        // Confirm API succeeded. The reservation timer will poll GetDetails,
                        // detect the Active status, and call confirmGiftVoucherSuccess()
                        // to navigate to /complete.
                    })
                    .catch((error: Error) => {
                        dispatch({ type: CONFIRM_GIFT_VOUCHER_FAILED });
                         appInsights?.trackException(
                             { error: new Error(`Error confirming gift voucher`), severityLevel: 2},
                             { exception: error, sourceFile: "GiftVoucher.tsx", product: "booking" });
                    });

                dispatch({ type: CONFIRM_GIFT_VOUCHER_COMPLETE });
            }

            dispatch({ type: CONFIRM_GIFT_VOUCHER_REQUESTED });
        },

    confirmGiftVoucherSuccess: (): IAppThunkAction<KnownGiftVoucherActions> => (dispatch, getState) => { // Confirm GiftVoucher redirect
        const appState: IApplicationState = getState();
        const giftVoucher = appState.giftVoucher.giftVoucher;
        const charge = appState.giftVoucher.charge;
        const isGlobal = appState.purchaseType.selectedPurchaseType === PurchaseTypes.GlobalGiftVoucher;

        if (charge && giftVoucher) {
            const siteCode = appState.sites.selectedSite?.code ?? '';
            const gtmKey = appState.sites.selectedSiteGtmKey;

            dispatch({
                type: CONFIRM_GIFT_VOUCHER_SUCCESS,
                googleTagManagerEvent: GoogleTagManagerEvents.ConfirmGiftVoucher(charge, giftVoucher, siteCode, isGlobal, gtmKey),
            });

            // for global gift voucher purchases we don't want to include the venue in the url
            const siteId = appState.sites.selectedSite?.siteId ?? undefined;
            if (siteId === Constants.GlobalGiftVoucherHostSiteId || siteId === null || siteId === undefined) {
                dispatch(navigateTo(Routes.Complete));
            }
            else {
                dispatch(navigateTo(Routes.Complete, appState.sites.selectedSite?.urlKey));
            }
        }
    },

    validateGiftVoucher:
        (onValidationSuccess: any, onValidationFailed: any): IAppThunkAction<KnownGiftVoucherActions> =>
            (dispatch, getState) => {

                //TODO this seems like a duplicate of the fetch method that just does one additional validation check on the returned data,
                // Refactor to be less redundant 

            const appState: IApplicationState = getState();
            const giftVoucher = appState.giftVoucher.giftVoucher;
            const siteId = appState.sites.selectedSite?.siteId;

            if (!(giftVoucher && siteId))
            {
                console.warn(`validateGiftVoucher called while giftVoucher was invalid: ${giftVoucher} or siteId was invalid: ${siteId}`);
                return;
            }

            dispatch({ type: FETCH_GIFT_VOUCHER_DETAILS_REQUESTED });

            GiftVoucherService.GetDetails(siteId, giftVoucher.giftVoucherRef)
            .then((data) => {
                //check if database voucher is marked as expired
                if (data.giftVoucher.giftVoucherStatusTypeId !== 5) {
                    onValidationSuccess();
                } else {
                    onValidationFailed();
                }
                dispatch({
                    type: FETCH_GIFT_VOUCHER_DETAILS_SUCCESS,
                    details: data.giftVoucher,
                });
            })
            .catch((exception) => {
                dispatch({ type: FETCH_GIFT_VOUCHER_DETAILS_FAILED });

                onValidationFailed();
                    appInsights?.trackException(
                        { error: new Error(`Error validating gift voucher`), severityLevel: 2},
                        { exception: exception, sourceFile: "GiftVoucher.tsx", product: "booking" });
            })
        },

    cancelGiftVoucher: (): IAppThunkAction<KnownGiftVoucherActions> => (dispatch, getState) => {
        const appState: IApplicationState = getState();
        const giftVoucher = appState.giftVoucher.giftVoucher;
        const charge = appState.giftVoucher.charge;
        const status = appState.giftVoucher.status;
        if (giftVoucher && status === GiftVoucherStatus.Pending) {
            GiftVoucherService.CancelGiftVoucher(giftVoucher.siteId, giftVoucher, charge)
                .then((_response) => {
                    dispatch({ type: CANCEL_GIFT_VOUCHER_SUCCESS });
                    dispatch({ type: CANCEL_GIFT_VOUCHER_COMPLETE });
                })

                .catch((error: Error) => {
                    dispatch({ type: CANCEL_GIFT_VOUCHER_FAILED });
                    dispatch({ type: CANCEL_GIFT_VOUCHER_COMPLETE });
                     appInsights?.trackException(
                         { error: new Error(`Error cancelling gift voucher`), severityLevel: 2},
                         { exception: error, sourceFile: "GiftVoucher.tsx", product: "booking" });
                });
        }

        dispatch({ type: CANCEL_GIFT_VOUCHER_REQUESTED });
    },

    clearGiftVoucher: (): IAppThunkAction<KnownGiftVoucherActions> => (dispatch) => {
        Logging.SetInvoiceId('');
        dispatch({ type: CLEAR_GIFT_VOUCHER });
    },

    recordToEmailAddress: (providedEmail: string) =>
        ({
            type: RECORD_TO_EMAIL_ADDRESS,
            email: providedEmail,
            googleTagManagerEvent: GoogleTagManagerEvents.RecordRecipientEmailAddress(),
        } as IToEmailFieldChangedAction),

    recordToFullName: (providedName: string) =>
        ({
            type: RECORD_TO_FULL_NAME,
            name: providedName,
            googleTagManagerEvent: GoogleTagManagerEvents.RecordRecipientFullName(),
        } as IToNameFieldChangedAction),

    recordMessage: (providedMessage: string) =>
        ({
            type: RECORD_MESSAGE,
            message: providedMessage,
            googleTagManagerEvent: GoogleTagManagerEvents.RecordRecipientMessage(),
        } as IMessageFieldChangedAction),

    setRecipientDetailsValid: (isValid: boolean) =>
        ({
            type: SET_RECIPIENT_DETAILS_VALID,
            isValid,
        } as ISetRecipientDetailsValidAction),

    enterAmount: (providedAmount: number, isGlobal: boolean) =>
        ({
            type: AMOUNT_ENTERED,
            amount: providedAmount,
            googleTagManagerEvent: GoogleTagManagerEvents.CustomGiftVoucherAmountInput(providedAmount, isGlobal),
        } as IAmountEnteredAction),

    buttonSelected: (providedAmount: number, selectedButtonIndex: number, isGlobal: boolean) =>
        ({
            type: BUTTON_SELECTED,
            amount: providedAmount,
        buttonIndex: selectedButtonIndex,
        googleTagManagerEvent: GoogleTagManagerEvents.FixedGiftVoucherAmountSelected(providedAmount, isGlobal),
        } as IButtonSelectedAction),

    clearField: () =>
        ({
            type: CLEAR_FIELD,
            amount: 0,
        } as IClearFieldAction),

    getGiftVoucherConfig:
        (isGlobal:boolean, siteId?: number, currencyId?: number): IAppThunkAction<KnownGiftVoucherActions> =>
        (dispatch, _getState) => {
            if ((isGlobal && currencyId !== undefined)
                || (!isGlobal && siteId !== undefined)) {
                GiftVoucherService.GetVoucherConfig(isGlobal, siteId, currencyId)
                    .then((data) => {
                        dispatch({
                            type: GIFT_VOUCHER_CONFIG_LOADED,
                            success: true,
                            details: data as IGiftVoucherConfig,
                        });
                    })
                    .catch((exception) => {
                        dispatch({
                            type: GIFT_VOUCHER_CONFIG_LOADED,
                            success: false,
                            details: undefined,
                        });
                         appInsights?.trackException(
                             { error: new Error(`Error getting gift voucher`), severityLevel: 2},
                             { exception: exception, sourceFile: "GiftVoucher.tsx", product: "booking" });
                    });
            }

            dispatch({ type: GIFT_VOUCHER_CONFIG_LOADING });
        },

    setRecipient: (toRecipient: boolean, isGlobal: boolean) =>
        ({
            type: RECIPIENT_SELECTED,
            toRecipient: toRecipient,
            googleTagManagerEvent: GoogleTagManagerEvents.GiftVoucherRecipientSelected(toRecipient, isGlobal),
        } as IRecipientSelectedAction),

    updateGiftVoucherDetails:
        (onSuccess: any, onFail: any): IAppThunkAction<KnownGiftVoucherActions> =>
            (dispatch, getState) => {

            // cancel previous request if one is running
            if (inFlightUpdateGiftVoucher) inFlightUpdateGiftVoucher.abort("updateGiftVoucherDetails called with newer state");

            // create per-call controller and hand over as current
            const controller = new AbortController();
            inFlightUpdateGiftVoucher = controller; 

            const appState: IApplicationState = getState();

            // Customer data is read from the Customer store — this gift voucher thunk currently
            // serves as the sync mechanism for customer details. See Customer.ts header for migration plan.
            const fromEmailAddress = appState?.customer?.emailAddress;
            const fromFullName = appState?.customer?.fullName;
            const toEmailAddress = appState.giftVoucher.toEmail;
            const toFullName = appState.giftVoucher.toFullName;
            const message = appState.giftVoucher.message;
            const toRecipient = appState.giftVoucher.toRecipientSelected;

            const finalToEmail = toRecipient ? toEmailAddress : fromEmailAddress;
            const finalToName = toRecipient ? toFullName : fromFullName;

            const phoneNumber = formatPhoneNumber(appState?.customer?.callingCode, appState?.customer?.phoneNumber) ?? null;

            if (appState && appState.giftVoucher.giftVoucher && fromEmailAddress) {
                const giftVoucher = appState.giftVoucher.giftVoucher;

                const params: IUpdateGiftVoucherRequest = {
                    fromEmail: fromEmailAddress,
                    fromName: fromFullName,
                    toEmail: finalToEmail,
                    toName: finalToName,
                    credit: null,
                    giftMessage: message,
                    phoneNumber: phoneNumber,
                };

                GiftVoucherService.UpdateGiftVoucher(
                    giftVoucher.giftVoucherRef,
                    giftVoucher.siteId,
                    params, 
                    controller.signal
                )
                .then((_data: any) => {
                    dispatch(CustomerActions.markSynced() as any);
                    onSuccess();
                })
                .catch((error: Error) => {
                    if ((error as any)?.name === 'AbortError') {
                        console.log("ignore cancelled api request in updateGiftVoucherDetails", error);
                        return; // ignore cancelled
                    }

                    onFail();

                    appInsights?.trackException(
                        { error: new Error(`Error updating gift voucher`), severityLevel: 2},
                        { exception: error, sourceFile: "GiftVoucher.tsx", product: "booking" });
                })
                .finally(() => {
                    // only clear if this request is still the active one
                    if (inFlightUpdateGiftVoucher === controller) inFlightUpdateGiftVoucher = null;
                });
            }
        },

    changeUser: (): IAppThunkAction<KnownGiftVoucherActions> => (dispatch, getState) => {
        const appState: IApplicationState = getState();

        // Customer data read from Customer store — see Customer.ts header for migration plan
        const fromEmailAddress = appState?.customer?.emailAddress;
        const fromFullName = appState?.customer?.fullName;
        const toEmailAddress = appState.giftVoucher.toEmail;
        const toFullName = appState.giftVoucher.toFullName;
        const message = appState.giftVoucher.message;
        const toRecipient = appState.giftVoucher.toRecipientSelected;

        const finalToEmail = toRecipient ? toEmailAddress : fromEmailAddress;
        const finalToName = toRecipient ? toFullName : fromFullName;

        const purchaseType = appState?.purchaseType?.selectedPurchaseType;

        //check if we have a valid venue for regular gift voucher
        //OR the global site is loaded for global gift voucher
        const useGlobalVenue = purchaseType === PurchaseTypes.GlobalGiftVoucher;
        const globalVenue = appState.sites.globalSite;
        const selectedVenue = appState.sites.selectedSite;

        const venueToUse = useGlobalVenue ? globalVenue : selectedVenue;

        const isVenueValid = (venueToUse && venueToUse !== undefined && venueToUse.siteId && venueToUse.name);

        const names = fromFullName?.split(' ') ?? [];
        const firstName = names[0] ?? '';
        const lastName = names.slice(1).join(' ') ?? '';

        const phoneNumber = formatPhoneNumber(appState?.customer?.callingCode, appState?.customer?.phoneNumber) ?? null;

        const currentLocale = appState.intl.selectedLanguage.code;

        if (appState && appState.giftVoucher.giftVoucher && fromEmailAddress && isVenueValid)
        {
            const giftVoucher = appState?.giftVoucher?.giftVoucher;

            const params: IUpdateGiftVoucherRequest = {
                fromEmail: fromEmailAddress,
                fromName: fromFullName,
                toEmail: finalToEmail,
                toName: finalToName,
                credit: null,
                giftMessage: message,
                phoneNumber: phoneNumber,
            };

            GiftVoucherService.UpdateGiftVoucher(
                giftVoucher.giftVoucherRef,
                giftVoucher.siteId,
                params
            )
            .then((data: any) => {
                dispatch({
                    type: CHANGE_USER,
                    giftVoucher: data.giftVoucher,
                    brazeEvent: BrazeEvents.ChangeUser(
                        data.giftVoucher.playerGuid,
                        fromEmailAddress,
                        firstName,
                        lastName,
                        venueToUse?.siteId,
                        venueToUse?.name,
                        phoneNumber,
                        venueToUse,
                        currentLocale
                    ),
                });
                dispatch(CustomerActions.markSynced() as any);
            })
                .catch((error: Error) => {
                    if ((error as any)?.name === 'AbortError') {
                        return;
                    }
                    console.log(`Error changing gift voucher user`);
                    appInsights?.trackException(
                        { error: new Error(`Error changing gift voucher user`), severityLevel: 2 },
                        {
                            exception: error,
                            sourceFile: "GiftVoucher.tsx",
                            product: "booking",
                            useGlobalVenue: useGlobalVenue,
                            venueId: venueToUse?.siteId,
                            venueName: venueToUse?.name,
                            giftVoucherId: giftVoucher?.giftVoucherId,
                            invoiceId: giftVoucher?.invoiceId,
                            voucherSiteId: giftVoucher?.siteId,
                            currencyCode: appState?.giftVoucher.selectedVoucherCurrency?.code
                        });
            });
        }
    },

    updateGiftVoucherAmount:
        (onComplete: () => void): IAppThunkAction<KnownGiftVoucherActions> =>
        (dispatch, getState) => {
            const appState: IApplicationState = getState();
            const amount = appState.giftVoucher.amount;

            if (appState && appState.giftVoucher.giftVoucher && amount) {
                const giftVoucher = appState.giftVoucher.giftVoucher;

                const params: IUpdateGiftVoucherRequest = {
                    credit: amount,
                };
                GiftVoucherService.UpdateGiftVoucher(
                    giftVoucher.giftVoucherRef,
                    giftVoucher.siteId,
                    params
                )
                .then((data) => {
                    dispatch({
                        type: UPDATE_GIFT_VOUCHER_AMOUNT_SUCCESS,
                        giftVoucher: data.giftVoucher,
                        charge: data.charge,
                    });
                    onComplete();
                })
                .catch((error: Error) => {
                    dispatch({
                        type: UPDATE_GIFT_VOUCHER_AMOUNT_FAILED,
                        failureMessageKey: 'update failed',
                    });
                        appInsights?.trackException(
                            { error: new Error(`Error updating gift voucher ammount`), severityLevel: 2},
                            { exception: error, sourceFile: "GiftVoucher.tsx", product: "booking" });
                });

                dispatch({
                    type: UPDATE_GIFT_VOUCHER_AMOUNT_LOADING,
                });
            }
        },

    fetchGiftVoucherDetails:
        (siteId: number, giftVoucherRef: string): IAppThunkAction<KnownGiftVoucherActions> =>
            (dispatch, getState) => {
                if (!giftVoucherRef) {
                    console.warn(`fetchGiftVoucherDetails called while giftVoucherRef was invalid: ${giftVoucherRef}`);
                    return;
                }

                dispatch({ type: FETCH_GIFT_VOUCHER_DETAILS_REQUESTED });

                GiftVoucherService.GetDetails(siteId, giftVoucherRef)
                    .then((data) => {
                        const appState = getState();
                        //if the total doesn't match update the local charge, ammount, and voucher to match the api return
                        if (!_.isEqual(appState.giftVoucher.charge?.total, data.charge.total)) {
                            dispatch({
                                type: UPDATE_GIFT_VOUCHER_AMOUNT_SUCCESS,
                                giftVoucher: data.giftVoucher,
                                charge: data.charge,
                            });
                        } else if (!_.isEqual(appState.giftVoucher.giftVoucher, data.giftVoucher)) {
                            //otherwise only update the local voucher record to match the api return
                            dispatch({
                                type: FETCH_GIFT_VOUCHER_DETAILS_SUCCESS,
                                details: data.giftVoucher,
                            });
                        }
                    })
                    .catch((exception) => {
                        dispatch({ type: FETCH_GIFT_VOUCHER_DETAILS_FAILED });

                        appInsights?.trackException(
                            { error: new Error(`Error getting gift voucher`), severityLevel: 2 },
                            { exception: exception, sourceFile: "GiftVoucher.tsx", product: "booking", siteId: siteId, giftVoucherRef: giftVoucherRef });
                    })
        },

    modifyGiftVoucher:
        (giftVoucher: IGiftVoucher): IAppThunkAction<KnownGiftVoucherActions> =>
        (dispatch, getState) => {
            const appState = getState();

            dispatch({
                type: MODIFY_GIFT_VOUCHER,
                googleTagManagerEvent: GoogleTagManagerEvents.ModifiedGiftVoucher(giftVoucher),
                brazeEvent: BrazeEvents.ModifyCart(),
            } as IModifyGiftVoucherAction);

            //redirect user back to the modify page for the type of gift voucher they are building
            if (appState.purchaseType.selectedPurchaseType === PurchaseTypes.GlobalGiftVoucher) {
                // TODO - back navigating to the global gifts page doesn't properly restore the users selections previously made
                dispatch(navigateTo(Routes.RegionalGiftVoucher));
            }
            else {
                dispatch(navigateTo(Routes.GiftVoucher, appState.sites.selectedSite?.urlKey));
            }
            },

    selectVoucherCurrency: (currency: ICurrency| undefined): IAppThunkAction<KnownGiftVoucherActions> =>
        (dispatch, _getState) => {
            dispatch({
                type: SELECT_VOUCHER_CURRENCY,
                currency: currency
            } as ISelectVoucherCurrency);
        }

};

export const reducer: Reducer<IGiftVoucherState> = (
    state: IGiftVoucherState | undefined,
    incomingAction: Action
): IGiftVoucherState => {
    const action: KnownGiftVoucherActions = incomingAction as KnownGiftVoucherActions;
    if (state === undefined) return undefinedState;

    switch (action.type) {

        case FETCH_GIFT_VOUCHER_DETAILS_REQUESTED: {
            return {
                ...state,
                loading: true,
                failed: false
            };
        }
        case FETCH_GIFT_VOUCHER_DETAILS_SUCCESS: {

            //grab out the new voucher statue and map it to the local enum
            var giftVoucherStatus = state.status;
            if (action.details && action.details.giftVoucherStatusTypeId !== undefined) {
                if (action.details.giftVoucherStatusTypeId in GiftVoucherStatus) {
                    giftVoucherStatus = action.details.giftVoucherStatusTypeId as GiftVoucherStatus;
                }
            }

            //grab the voucher ammount
            //Seems redundant, why would the api not return the voucher amount ?
            const newAmount = action.details?.credit ?? state.amount;
            // ALSO seems to be more redundant given that UPDATE_GIFT_VOUCHER_AMOUNT_SUCCESS updates the voucher and amount and charge
            // based on the same underlying api endpoint call

            return {
                ...state,
                loading: false,
                failed: false,
                giftVoucher: action.details,
                amount: newAmount,
                status: giftVoucherStatus,
            };
        }
        case FETCH_GIFT_VOUCHER_DETAILS_FAILED: {

            return {
                ...state,
                loading: false,
                failed: true
            };
        }

        case GIFT_VOUCHER_CONFIG_LOADING: {
            return {
                ...state,
                loading: false,
                configLoading: true,
                failed: false,
                configDetails: undefined,
            };
        }
        case GIFT_VOUCHER_CONFIG_LOADED: {
            return {
                ...state,
                loading: false,
                configLoading: false,
                failed: !action.success,
                configDetails: action.details,
            };
        }

        case RECORD_TO_EMAIL_ADDRESS: {
            return {
                ...state,
                toEmail: action.email,
            };
        }

        case RECORD_TO_FULL_NAME: {
            return {
                ...state,
                toFullName: action.name,
            };
        }

        case RECORD_MESSAGE: {
            return {
                ...state,
                message: action.message,
            };
        }

        case SET_RECIPIENT_DETAILS_VALID: {
            return {
                ...state,
                recipientDetailsValid: action.isValid,
            };
        }

        case AMOUNT_ENTERED: {
            return {
                ...state,
                selectedFixedAmountButtonIndex: undefined,
                amount: action.amount,
                selectedGiftVoucherAmountUI: true,
            };
        }

        case BUTTON_SELECTED: {
            return {
                ...state,
                selectedFixedAmountButtonIndex: action.buttonIndex,
                amount: action.amount,
                selectedGiftVoucherAmountUI: true,
            };
        }

        case CLEAR_FIELD: {
            return {
                ...state,
                amount: 0,
                selectedGiftVoucherAmountUI: true,
            };
        }

        case RECIPIENT_SELECTED: {
            return {
                ...state,
                toRecipientSelected: action.toRecipient,
                recipientDetailsValid: action.toRecipient ? false : state.recipientDetailsValid,
            };
        }

        case RESERVE_GIFT_VOUCHER_SUCCESS: {
            return {
                ...state,
                loading: false,
                failed: false,
                charge: action.charge,
                giftVoucher: action.giftVoucher,
                status: GiftVoucherStatus.Pending,
            };
        }

        case RESERVE_GIFT_VOUCHER_FAILED: {
            return {
                ...state,
                loading: false,
                failed: true,
                charge: undefined,
                giftVoucher: undefined,
            };
        }

        case RESERVE_GIFT_VOUCHER_REQUESTED: {
            return {
                ...state,
                loading: true,
                failed: false,
                charge: undefined,
                giftVoucher: undefined,
            };
        }

        case UPDATE_GIFT_VOUCHER_AMOUNT_SUCCESS: {
            return {
                ...state,
                loading: false,
                failed: false,
                charge: action.charge,
                giftVoucher: action.giftVoucher,
                amount: action.giftVoucher.credit,
            };
        }

        case UPDATE_GIFT_VOUCHER_AMOUNT_FAILED: {
            return {
                ...state,
                loading: false,
                failed: true,
            };
        }

        case UPDATE_GIFT_VOUCHER_AMOUNT_LOADING: {
            return {
                ...state,
                loading: true,
                failed: false,
            };
        }

        case CANCEL_GIFT_VOUCHER_REQUESTED: {
            return {
                ...state,
                loading: true,
                failed: false,
            };
        }

        case CANCEL_GIFT_VOUCHER_SUCCESS: {
            return {
                ...state,
                loading: false,
                failed: false,
                giftVoucher: undefined,
                charge: undefined,
                status: GiftVoucherStatus.Unknown,
                selectedFixedAmountButtonIndex: undefined,
                amount: undefined,
                toEmail: undefined,
                toFullName: undefined,
                message: undefined,
                recipientDetailsValid: false,
            };
        }
        case CANCEL_GIFT_VOUCHER_COMPLETE: {
            return {
                ...state,
                loading: false,
                failed: state.failed,
                giftVoucher: undefined,
                charge: undefined,
                status: GiftVoucherStatus.Unknown,
                selectedFixedAmountButtonIndex: undefined,
                amount: undefined,
                toEmail: undefined,
                toFullName: undefined,
                message: undefined,
                recipientDetailsValid: false,
            };
        }
        case CONFIRM_GIFT_VOUCHER_REQUESTED: {
            return {
                ...state,
                loading: true,
                failed: false,
                status: GiftVoucherStatus.Completed,
            };
        }
        case CONFIRM_GIFT_VOUCHER_FAILED: {
            return {
                ...state,
                loading: false,
                failed: true,
                status: GiftVoucherStatus.Completed,
            };
        }
        case CONFIRM_GIFT_VOUCHER_SUCCESS: {
            return {
                ...state,
                loading: false,
                failed: false,
                status: GiftVoucherStatus.Completed,
            };
        }
        case CLEAR_GIFT_VOUCHER: {
            return {
                ...state,
                ...undefinedState,
                configDetails: state.configDetails,
            };
        }
        case CHANGE_USER: {
            return {
                ...state,
                giftVoucher: action.giftVoucher,
            };
        }
        case SELECT_VOUCHER_CURRENCY: {
            return {
                ...state,
                selectedVoucherCurrency: action.currency,
                selectedFixedAmountButtonIndex: undefined,
                amount: undefined,
            };
        }

        default:
            return state;
    }
};
